import { Box, Camera, ChevronDown, ChevronRight, Circle, Eye, EyeOff, Group, Lightbulb, Lock, LockOpen, Plus, Square } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Layer, SceneObject } from '../../core/schema';
import { createId, defaultTransform } from '../../core/project';
import { useEditorStore } from '../../store/editorStore';

function SceneIcon({ object }: { object: SceneObject }) {
  if (object.type === 'light') return <Lightbulb size={13} />;
  if (object.type === 'camera') return <Camera size={13} />;
  if (object.type === 'sphere') return <Circle size={13} />;
  if (object.type === 'group') return <Group size={13} />;
  return <Box size={13} />;
}

export function LayerOutliner() {
  const project = useEditorStore((state) => state.project);
  const selectedIds = useEditorStore((state) => state.selectedLayerIds);
  const selectLayer = useEditorStore((state) => state.selectLayer);
  const updateLayer = useEditorStore((state) => state.updateLayer);
  const addLayer = useEditorStore((state) => state.addLayer);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const composition = project.compositions[project.activeCompositionId];
  const layers = useMemo(() => composition.layerIds.map((id) => project.layers[id]).filter(Boolean), [composition.layerIds, project.layers]);

  const addObject = (layer: Layer, type: SceneObject['type']) => updateLayer(layer.id, (draft) => {
    if (!draft.scene) return;
    draft.scene.objects.push({ id: createId('obj'), name: type[0].toUpperCase() + type.slice(1), type, parentId: null, visible: true, locked: false, transform: defaultTransform(), ...(type !== 'group' && type !== 'camera' ? { material: { color: '#9ca7ff', metallic: 0.15, roughness: 0.55, emission: '#000000', opacity: 1 } } : {}) });
  });

  return <div className="panel-content outliner-panel">
    <div className="panel-heading"><div><span className="eyebrow">SCENE</span><h2>Outliner</h2></div><button className="square-button" onClick={() => addLayer('scene3d')} title="Add 3D scene"><Plus size={15} /></button></div>
    <div className="outliner-root"><ChevronDown size={13} /><span className="comp-icon"><Square size={12} /></span><b>{composition.name}</b><span className="outliner-count">{layers.length}</span></div>
    <div className="outliner-list">{layers.map((layer) => {
      const hasChildren = layer.type === 'scene3d' && Boolean(layer.scene?.objects.length);
      const open = expanded[layer.id] ?? true;
      return <div key={layer.id}>
        <div className={`outliner-row ${selectedIds.includes(layer.id) ? 'is-selected' : ''}`} onClick={(event) => selectLayer(layer.id, event.metaKey || event.ctrlKey)}>
          <button onClick={(event) => { event.stopPropagation(); setExpanded({ ...expanded, [layer.id]: !open }); }}>{hasChildren ? open ? <ChevronDown size={12} /> : <ChevronRight size={12} /> : null}</button>
          <span className={`layer-type-dot type-${layer.type}`} />
          <span>{layer.name}</span>
          <button onClick={(event) => { event.stopPropagation(); updateLayer(layer.id, { visible: !layer.visible }); }}>{layer.visible ? <Eye size={12} /> : <EyeOff size={12} />}</button>
          <button onClick={(event) => { event.stopPropagation(); updateLayer(layer.id, { locked: !layer.locked }); }}>{layer.locked ? <Lock size={11} /> : <LockOpen size={11} />}</button>
        </div>
        {hasChildren && open && <div className="scene-children">{layer.scene!.objects.map((object) => <div className="outliner-row scene-object-row" key={object.id}>
          <span /><SceneIcon object={object} /><span>{object.name}</span><span /><span />
        </div>)}<div className="object-add-row"><button onClick={() => addObject(layer, 'cube')}>+ Cube</button><button onClick={() => addObject(layer, 'sphere')}>+ Sphere</button><button onClick={() => addObject(layer, 'light')}>+ Light</button></div></div>}
      </div>;
    })}</div>
  </div>;
}
